import { Label } from "@/shared/ui/label";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Separator } from "@/shared/ui/separator";
import { UnifiedUpload } from "@/shared/ui/UnifiedUpload";
import { toast } from "@/shared/hooks/use-toast";
import { CustomSwitch } from "@/shared/widgets/switch";
import { CheckIcon, XIcon } from "lucide-react";
import { ReactSelect } from "@/shared/widgets";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { TagsService, Tag, LanguageCode } from "@/features/tags/service/tagService";
import { useCategoriesData } from "@/features/categories/hooks/useCategoriesData";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "@/shared/i18n";

interface PresetTag {
  id: string;
  tagId: string;
  presetId: string;
  deleted: boolean;
  tag: Tag;
}

interface PresetBasicInfoProps {
  presetId: string | null;
  isEditing: boolean;
  name: string;
  tags: string;
  setTags: (tags: string) => void;
  presetsTags?: PresetTag[];
  onPresetsTagsChange?: (presetsTags: PresetTag[]) => void;
  description: string;
  coverPreview: string | null;
  lutFilename: string | null;
  lutPreview?: string | null;
  published: boolean;
  nameError: boolean;
  tagsError: boolean;
  coverError: boolean;
  lutError: boolean;
  descriptionError: boolean;
  isFree: boolean;
  handleIsFreeChange: (checked: boolean) => void;
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTagsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handlePublishedChange: (checked: boolean) => void;
  handleCoverChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLutChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCover: () => void;
  onRemoveLut: () => void;
  // Category props
  selectedCategoryIds: string[];
  setSelectedCategoryIds: (categoryIds: string[]) => void;
  categoryError?: boolean;
}

export const PresetBasicInfo = ({
  presetId,
  isEditing,
  name,
  tags,
  setTags,
  presetsTags,
  onPresetsTagsChange,
  description,
  coverPreview,
  lutFilename,
  lutPreview = null,
  published,
  nameError,
  tagsError,
  coverError,
  lutError,
  descriptionError,
  isFree,
  handleIsFreeChange,
  handleNameChange,
  handleTagsChange,
  handleDescriptionChange,
  handlePublishedChange,
  handleCoverChange,
  handleLutChange,
  onRemoveCover,
  onRemoveLut,
  selectedCategoryIds,
  setSelectedCategoryIds,
  categoryError = false,
}: PresetBasicInfoProps) => {
  const { t } = useTranslation('presets');

  // Estados para gerenciar tags
  const [selectedTags, setSelectedTags] = useState<{ value: string; label: string; }[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState<string>("");

  // Estados para gerenciar busca de categorias (filtro local)
  const [categorySearchQuery, setCategorySearchQuery] = useState<string>("");

  // Buscar todas as categorias de uma vez (limit: 9999) para evitar requisições frequentes
  const { data: categoriesResponse, isLoading: isLoadingCategories, refetch: refetchCategories } = useCategoriesData({ 
    type: "PRESET",
    limit: 9999 // Buscar todas as categorias de uma vez
  });

  // Ref para manter o estado anterior das tags (para comparação)
  const previousTagsRef = useRef<string[]>([]);

  // Custom query para buscar tags
  const { data: tagsResponse, isLoading: isLoadingTags } = useCustomQuery({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.getTags,
    parameter: tagSearchQuery.length >= 2 ? { search: tagSearchQuery, limit: 20 } : undefined,
  }) as { data: { data: Tag[]; page: number; limit: number; totalPage: number; totalItems: number } | undefined; isLoading: boolean };

  // Mutations para vincular/desvincular tags
  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.linkTagToPreset,
  });

  const unlinkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.unlinkTagFromPreset,
  });

  // Converte os dados das tags para o formato do ReactSelect
  const tagOptions = useMemo(() => {
    const tagsData = tagsResponse?.data || [];
    return tagsData.map((tag: Tag) => ({
      value: tag.id,
      label: tag.translations.find(t => t.languageCode === 'PT_BR')?.name || tag.translations[0]?.name || tag.id,
    }));
  }, [tagsResponse]);

  // Converte os dados das categorias para o formato do ReactSelect e filtra localmente
  const categoryOptions = useMemo(() => {
    const categoriesData = categoriesResponse?.data || [];
    const allCategories = categoriesData.map((category) => ({
      value: category.id,
      label: category.name,
    }));

    // Filtro local baseado no que o usuário digita
    if (!categorySearchQuery.trim()) {
      return allCategories;
    }

    const searchLower = categorySearchQuery.toLowerCase().trim();
    const filteredCategories = allCategories.filter((category) =>
      category.label.toLowerCase().includes(searchLower)
    );

    // Garantir que as categorias selecionadas sempre estejam nas opções, mesmo que não correspondam ao filtro
    const selectedCategories = allCategories.filter((category) =>
      selectedCategoryIds.includes(category.value || '')
    );

    // Combinar categorias filtradas com categorias selecionadas, removendo duplicatas
    const combined = [...filteredCategories];
    selectedCategories.forEach((selected) => {
      if (!combined.find((cat) => cat.value === selected.value)) {
        combined.push(selected);
      }
    });

    return combined;
  }, [categoriesResponse, categorySearchQuery, selectedCategoryIds]);

  // Função para buscar tags (atualiza o query)
  const searchTags = (query: string) => {
    setTagSearchQuery(query);
  };

  // Função para buscar categorias (atualiza o query)
  const searchCategories = (query: string) => {
    setCategorySearchQuery(query);
  };

  // Função para gerenciar mudanças nas tags
  const handleTagsSelectChange = async (newValue: string | string[]) => {
    const newTagValues = Array.isArray(newValue) ? newValue : [];
    const formattedTags = newTagValues.map(value => ({
      value,
      label: tagOptions.find(opt => opt.value === value)?.label || value
    }));

    // Atualiza o estado local das tags selecionadas
    setSelectedTags(formattedTags);

    // Atualiza o campo tags do formulário (para compatibilidade)
    const tagsString = newTagValues.join(',');
    setTags(tagsString);

    // Para presets novos (sem ID), apenas atualiza o estado local
    if (!presetId && !isEditing) {
      // Atualiza o ref para a próxima comparação
      previousTagsRef.current = newTagValues;
      return;
    }

    if (!presetId) {
      console.warn('Preset ID não encontrado para vincular tags');
      return;
    }

    // Usa o estado anterior das tags para comparação
    const currentTagValues = previousTagsRef.current;

    // Tags adicionadas (novas seleções)
    const addedTags = newTagValues.filter(tagId => !currentTagValues.includes(tagId));

    // Tags removidas (seleções removidas)
    const removedTags = currentTagValues.filter(tagId => !newTagValues.includes(tagId));

    // Atualiza o ref para a próxima comparação
    previousTagsRef.current = newTagValues;

    // Vincula novas tags
    for (const tagId of addedTags) {
      try {
        await linkTagMutation.mutateAsync({
          tagId,
          presetId: presetId
        });

        // Adiciona a tag à memória local (presetsTags)
        if (onPresetsTagsChange && presetsTags) {
          // Busca a tag completa nos dados disponíveis
          const tagData = tagOptions.find(option => option.value === tagId);
          if (tagData) {
            const newPresetTag: PresetTag = {
              id: `temp-${tagId}-${Date.now()}`, // ID temporário
              tagId: tagId,
              presetId: presetId,
              deleted: false,
              tag: {
                id: tagId,
                published: true,
                isCategory: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                translations: [{
                  languageCode: LanguageCode.PT_BR,
                  name: tagData.label
                }]
              }
            };

            const updatedPresetsTags = [...presetsTags, newPresetTag];
            onPresetsTagsChange(updatedPresetsTags);
          }
        }
      } catch (error) {
        console.error(`Erro ao vincular tag ${tagId}:`, error);
      }
    }

    // Desvincula tags removidas
    for (const tagId of removedTags) {
      try {
        await unlinkTagMutation.mutateAsync({
          tagId,
          presetId: presetId
        });

        // Remove a tag da memória local (presetsTags)
        if (onPresetsTagsChange && presetsTags) {
          const updatedPresetsTags = presetsTags.filter(presetTag => presetTag.tagId !== tagId);
          onPresetsTagsChange(updatedPresetsTags);
        }
      } catch (error) {
        console.error(`Erro ao desvincular tag ${tagId}:`, error);
      }
    }
  };

  // Inicializa tags selecionadas com valores padrão
  useEffect(() => {

    if (presetsTags && presetsTags.length > 0) {
      // Usa presetsTags para preencher as tags selecionadas
      const formattedTags = presetsTags
        .filter(presetTag => !presetTag.deleted && presetTag.tag) // Filtra tags não deletadas e com tag válida
        .map(presetTag => ({
          value: presetTag.tagId,
          label: presetTag.tag?.translations?.find(t => t.languageCode === 'PT_BR')?.name ||
            presetTag.tag?.translations?.[0]?.name ||
            presetTag.tagId
        }));

      setSelectedTags(formattedTags);

      // Atualiza o ref com os valores das tags
      const tagValues = formattedTags.map(tag => tag.value);
      previousTagsRef.current = tagValues;
    } else if (tags) {
      // Fallback para o campo tags antigo (compatibilidade)
      const tagsArray = tags.split(',').map(tag => tag.trim()).filter(Boolean);

      const formattedTags = tagsArray.map(tag => ({
        value: tag,
        label: tag
      }));

      setSelectedTags(formattedTags);

      // Atualiza o ref com os valores das tags
      previousTagsRef.current = tagsArray;
    }
  }, [presetsTags, tags, presetId]);
  return (
    <div>
      <h2 className="text-xl text-white font-semibold mb-2">{t('presetInfo')}</h2>
      <p className="text-sm text-white/60 mb-4">
        {t('presetInfoDescription')}
      </p>
      <Separator className="bg-white/10 mb-6" />

      {/* ID */}
      {isEditing && presetId && (
        <div className="mb-6">
          <Label className="text-white" htmlFor="preset-id">
            {t('presetId')}
          </Label>
          <Input
            id="preset-id"
            value={presetId}
            readOnly
            className="bg-white/[0.03] border-white/[0.06] text-white font-mono"
          />
          <p className="text-xs text-white/60 mt-1">
            {t('presetIdDescription')}
          </p>
        </div>
      )}

      {/* Switches */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">

      </div>

      <div className="mb-8">

        <div className="grid grid-cols-1 3x1:grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Capa + Switch */}
          <div className="grid grid-cols-2 justify-center items-center rounded-xl gap-4 bg-white/5 p-6">
            <UnifiedUpload
              id="cover"
              title={t('cover')}
              preview={coverPreview}
              onFileChange={handleCoverChange}
              onRemove={onRemoveCover}
              error={coverError}
              variant="cover"
              enableDragDrop={true}
              acceptTypes="image/*"
              maxSizeMB={0.5}
              onError={(error) =>
                toast({
                  title: t('error'),
                  description: error,
                  variant: "destructive",
                })
              }
            />
            <UnifiedUpload
              id="lut"
              title={t('lut')}
              preview={lutPreview || lutFilename}
              filename={lutFilename}
              onFileChange={handleLutChange}
              onRemove={onRemoveLut}
              acceptTypes=".cube,.3dl,.lut,.jpg,.jpeg,.png"
              error={lutError}
              variant="lut"
              enableDragDrop={true}
              maxSizeMB={0.5}
              onError={(error) =>
                toast({
                  title: t('error'),
                  description: error,
                  variant: "destructive",
                })
              }
            />

          </div>

          {/* LUT + Switch */}
          <div className="flex flex-col gap-4">

            <CustomSwitch
              checked={published}
              onChange={handlePublishedChange}
              id="published"
              label={t('published')}
              descriptionOn={t('publishedOn')}
              descriptionOff={t('publishedOff')}
              iconOn={<CheckIcon className="w-5 h-5 text-green-500" />}
              iconOff={<XIcon className="w-5 h-5 text-red-500" />}
              className="bg-white/10"
            />
            <CustomSwitch
              checked={isFree}
              onChange={handleIsFreeChange}
              id="isFree"
              label={t('isFree')}
              descriptionOn={t('isFreeOn')}
              descriptionOff={t('isFreeOff')}
              iconOn={<CheckIcon className="w-5 h-5 text-green-500" />}
              iconOff={<XIcon className="w-5 h-5 text-red-500" />}
              className="bg-white/10"
            />
          </div>
        </div>
      </div>

      {/* Nome, Tags e Categoria */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="space-y-2">
          <Label className="text-white" htmlFor="name">
            {t('name')}
          </Label>
          <Input
            id="name"
            value={name}
            onChange={handleNameChange}
            placeholder={t('namePlaceholder')}
            required
            className={`bg-white/[0.03] border-white/[0.06] text-white ${nameError ? "border-red-500" : ""
              }`}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white" htmlFor="tags">
            {t('tags')}
          </Label>
          {/* ReactSelect customizado com busca assíncrona para tags */}
          <ReactSelect
            options={tagOptions}
            value={selectedTags.map(tag => tag.value)}
            onChange={(newValue) => {
              console.log('ReactSelect onChange chamado:', newValue);
              handleTagsSelectChange(newValue);
            }}
            multiple
            searchable
            onSearch={searchTags}
            loading={isLoadingTags || linkTagMutation.isPending || unlinkTagMutation.isPending}
            placeholder={t('tagsPlaceholder')}
            noOptionsMessage={isLoadingTags ? t('tagsSearching') : t('tagsMinSearch')}
            minSearchLength={2}
            error={tagsError}
            errorMessage={tagsError ? t('tagsError') : undefined}
          />
          {/* Campo hidden para manter compatibilidade com o formulário */}
          <input
            type="hidden"
            name="tags"
            value={selectedTags.map(tag => tag.value).join(',')}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white" htmlFor="category">
            {t('category')}
          </Label>
          <ReactSelect
            options={categoryOptions}
            value={selectedCategoryIds}
            onChange={(newValue) => {
              const categoryIds = Array.isArray(newValue) ? newValue : [];
              setSelectedCategoryIds(categoryIds);
            }}
            multiple={true}
            searchable
            onSearch={searchCategories}
            loading={isLoadingCategories}
            placeholder={t('categoryPlaceholder')}
            noOptionsMessage={isLoadingCategories ? t('categorySearching') : t('categoryNotFound')}
            minSearchLength={0}
            error={categoryError}
            errorMessage={categoryError ? t('categoryRequired') : undefined}
          />
        </div>
      </div>

      {/* Descrição */}
      <div className="space-y-2">
        <Label className="text-white" htmlFor="description">
          {t('description')}
        </Label>
        <Textarea
          id="description"
          value={description}
          onChange={handleDescriptionChange}
          placeholder={t('descriptionPlaceholder')}
          className={`bg-white/[0.03] border-white/[0.06] text-white min-h-[100px] ${descriptionError ? "border-red-500" : ""
            }`}
        />
      </div>
    </div>
  );
};
